import React, { useMemo } from "react";
import { useGridApiContext } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

/**
 * Custom loading overlay that renders skeleton rows matching the actual column structure.
 * Uses DataGrid's API context to get visible columns and their computed widths.
 */
export function LoadingOverlay() {
  const apiRef = useGridApiContext();

  // @ts-expect-error Function signature expects parameters, but implementation works without them
  const rowHeight = apiRef.current.unstable_getRowHeight();
  const columns = apiRef.current.getVisibleColumns();
  const { pageSize } = apiRef.current.state.pagination.paginationModel;

  // Skeleton height: ~40% of row height for natural text appearance
  const skeletonHeight = Math.round(rowHeight * 0.4);

  const skeletonCells = useMemo(() => {
    const cells: React.ReactNode[] = [];
    const rowCount = pageSize || 10;

    for (let i = 0; i < rowCount; i++) {
      for (const column of columns) {
        cells.push(
          <Box
            key={`${i}-${column.field}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: column.align,
            }}
          >
            <Skeleton
              animation="wave"
              sx={{ mx: 1 }}
              width="100%"
              height={skeletonHeight}
            />
          </Box>
        );
      }
      // Filler cell for remaining space
      cells.push(<Box key={`${i}-filler`} />);
    }
    return cells;
  }, [columns, pageSize, skeletonHeight]);

  return (
    <Box
      display="grid"
      gridAutoRows={rowHeight}
      gridTemplateColumns={`${columns.map(({ computedWidth }) => `${computedWidth}px`).join(" ")} 1fr`}
    >
      {skeletonCells}
    </Box>
  );
}
